/**
 * @file: usePresence.ts
 *
 * Read-side of user presence. `useIsOnline` reports whether one identity is
 * online; `usePresenceRefresh` seeds the session's presence set for the
 * identities a surface displays (the live `presence` event subscription in
 * the session keeps them current after the initial snapshot).
 */

import { UserPresenceRef } from '@euler/model/chat';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useEffect } from 'react';
import { useChatSession } from '../context';
import { presenceKeyOf } from '../presence';

/** Whether a given identity is currently online (`null`/`undefined` → false). */
export function useIsOnline(ref: UserPresenceRef | null | undefined): boolean {
  const { presence$ } = useChatSession();
  const [online] = useBehaviorSubject(presence$);
  return ref ? online.has(presenceKeyOf(ref)) : false;
}

/** Seed/refresh presence for the given identities while mounted. */
export function usePresenceRefresh(refs: UserPresenceRef[]): void {
  const { refreshPresence } = useChatSession();
  // Re-query only when the identity set changes, not on every render.
  const key = refs.map(presenceKeyOf).sort().join(',');
  useEffect(() => {
    if (refs.length) refreshPresence(refs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshPresence]);
}
